import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { ServiceService } from '../../service.service';
import Swal from 'sweetalert2';
import { NgSelectModule } from '@ng-select/ng-select';

interface Employee {
  employee_id: string;
  employee_name: string;
  type?: string;
}

interface Customer {
  customer_id: string;
  customer_name: string;
}

interface Survey {
  survey_id: string;
  survey_number: string;
  survey_title: string;
  status: string;
}

interface SelectedCustomer {
  customer_id: string;
  customer_name: string;
}

@Component({
  selector: 'app-assignment',
  templateUrl: './assignment.component.html',
  styleUrls: ['./assignment.component.css']
})
export class CustomerSurveyAssignmentComponent implements OnInit {

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  loading = false;

  // Dropdowns
  surveyDropdown: Survey[] = [];
  employeeDropdown: Employee[] = [];
  customerDropdown: Customer[] = [];

  // Selected values
  selectedSurvey: string = '';
  selectedEmployee: Employee | null = null;  // ✅ Changed from array to single object
  selectedCustomers: Customer[] = [];

  // Search terms
  employeeSearchTerm: string = '';
  customerSearchTerm: string = '';

  // Filtered lists
  filteredEmployees: Employee[] = [];
  filteredCustomers: Customer[] = [];

  constructor(
    private apiService: ApiService,
    private serviceService: ServiceService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadSurveys();
    this.loadEmployees();
    this.loadCustomers();
  }

  loadSurveys(): void {
    this.apiService.get('FactoryCustomerSurvey/viewCreatedQuestionnaires').subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          // Only show surveys that are Pending or In Progress
          this.surveyDropdown = response.data.filter((s: any) => 
            s.status === 'Pending' || s.status === 'In Progress'
          ).map((survey: any) => ({
            survey_id: survey.survey_id,
            survey_number: survey.survey_number,
            survey_title: survey.survey_title,
            status: survey.status
          }));
        }
      },
      error: (error) => {
        console.error('Error loading surveys:', error);
        this.Toast.fire({
          icon: 'error',
          title: 'Failed to load surveys'
        });
      }
    });
  }

  loadEmployees(): void {
    this.serviceService.ViewAllEmployee().subscribe({
      next: (response: any) => {
        let allEmployees: Employee[] = [];

        if (response.sucess === 'true' && response.data) {
          allEmployees = response.data;
        } else if (response.success === true && response.data) {
          allEmployees = response.data;
        } else if (response.Data) {
          allEmployees = response.Data;
        } else if (Array.isArray(response)) {
          allEmployees = response;
        }

        console.log('Employees API:', allEmployees);

        // ✅ NO FILTER
        this.employeeDropdown = allEmployees;
      }
    });
  }

  loadCustomers(): void {
    this.serviceService.customerDropdown().subscribe({
      next: (response: any) => {
        let customers: Customer[] = [];
        
        if (response.sucess === 'true' && response.data) {
          customers = response.data;
        } else if (response.success === true && response.data) {
          customers = response.data;
        } else if (response.Data) {
          customers = response.Data;
        } else if (Array.isArray(response)) {
          customers = response;
        }

        this.customerDropdown = customers;
        this.filteredCustomers = [...this.customerDropdown];
      },
      error: (error) => {
        console.error('Error loading customers:', error);
      }
    });
  }

  // Employee selection - ✅ Updated for single selection
  onEmployeeSearch(): void {
    if (!this.employeeSearchTerm) {
      this.filteredEmployees = [...this.employeeDropdown];
    } else {
      this.filteredEmployees = this.employeeDropdown.filter(emp =>
        emp.employee_name.toLowerCase().includes(this.employeeSearchTerm.toLowerCase()) ||
        emp.employee_id.toLowerCase().includes(this.employeeSearchTerm.toLowerCase())
      );
    }
  }

  // ✅ Removed - No longer needed for single selection
  // selectEmployee method is not needed when using [(ngModel)]

  // ✅ Updated to clear single employee
  removeEmployee(): void {
    this.selectedEmployee = null;
  }

  // Customer selection
  onCustomerSearch(): void {
    if (!this.customerSearchTerm) {
      this.filteredCustomers = [...this.customerDropdown];
    } else {
      this.filteredCustomers = this.customerDropdown.filter(cust =>
        cust.customer_name.toLowerCase().includes(this.customerSearchTerm.toLowerCase()) ||
        cust.customer_id.toLowerCase().includes(this.customerSearchTerm.toLowerCase())
      );
    }
  }

  selectCustomer(customer: Customer): void {
    // Check if already selected
    const alreadySelected = this.selectedCustomers.find(
      c => c.customer_id === customer.customer_id
    );

    if (alreadySelected) {
      this.Toast.fire({
        icon: 'warning',
        title: 'Customer already selected'
      });
      return;
    }

    this.selectedCustomers.push({
      customer_id: customer.customer_id,
      customer_name: customer.customer_name
    });

    this.customerSearchTerm = '';
    this.filteredCustomers = [...this.customerDropdown];
  }

  removeCustomer(customerId: string): void {
    this.selectedCustomers = this.selectedCustomers.filter(
      c => c.customer_id !== customerId
    );
  }

  // ✅ Updated discard all
  discardAll(): void {
    Swal.fire({
      title: 'Discard All?',
      text: 'This will clear all selected employee and customers',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, discard'
    }).then((result) => {
      if (result.isConfirmed) {
        this.selectedEmployee = null;  // ✅ Changed
        this.selectedCustomers = [];
        this.selectedSurvey = '';
        this.Toast.fire({
          icon: 'success',
          title: 'All selections cleared'
        });
      }
    });
  }

  onSubmit(): void {
  // Validation
  if (!this.selectedSurvey) {
    this.Toast.fire({
      icon: 'error',
      title: 'Please select a survey'
    });
    return;
  }

  if (!this.selectedEmployee) {
    this.Toast.fire({
      icon: 'error',
      title: 'Please select an employee'
    });
    return;
  }

  if (this.selectedCustomers.length === 0) {
    this.Toast.fire({
      icon: 'error',
      title: 'Please select at least one customer'
    });
    return;
  }

  // Confirmation dialog
  Swal.fire({
    title: 'Submit Assignment?',
    html: `
      <p><strong>Survey:</strong> ${this.getSurveyTitle()}</p>
      <p><strong>Employee:</strong> ${this.selectedEmployee.employee_name}</p>
      <p><strong>Customers:</strong> ${this.selectedCustomers.length}</p>
      <p class="text-muted">This will assign ${this.selectedCustomers.length} customer(s) to ${this.selectedEmployee.employee_name}</p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, assign!'
  }).then((result) => {
    if (result.isConfirmed) {
      this.submitAssignment(); // Calls the submit method
    }
  });
}

submitAssignment(): void {
  this.loading = true;

  // Create assignment payload for single employee
  const assignments = this.selectedCustomers.map(customer => ({
    customer_id: customer.customer_id,
    customer_name: customer.customer_name,
    assigned_to_employee: this.selectedEmployee!.employee_id
  }));

  const payload = {
    survey_id: this.selectedSurvey,
    assignments: assignments
  };

  console.log('Assignment payload:', payload);

  // THIS IS ALREADY CALLING THE CORRECT API
  this.apiService.post('FactoryCustomerSurvey/addAssignments', payload).subscribe({
    next: (response: any) => {
      console.log('Assignment successful:', response);
      this.Toast.fire({
        icon: 'success',
        title: 'Assignment submitted successfully!'
      });
      this.loading = false;
      
      // Reset form
      this.selectedSurvey = '';
      this.selectedEmployee = null;
      this.selectedCustomers = [];
      
      // Navigate to feedback page (or stay on assignment page)
      // Option 1: Navigate to feedback
      // this.router.navigate(['admin/customer-survey-menu/feedback']);
      
      // Option 2: Just stay on the same page (recommended)
      // Don't navigate anywhere, form is already reset
    },
    error: (error) => {
      console.error('Error submitting assignment:', error);
      this.Toast.fire({
        icon: 'error',
        title: error.error?.message || 'Failed to submit assignment'
      });
      this.loading = false;
    }
  });
}


  onCustomerDropdownChange(event: any): void {
    const custId = event.target.value;
    if (!custId) return;

    const customer = this.customerDropdown.find(c => c.customer_id === custId);
    if (customer) {
      this.selectCustomer(customer);
    }

    event.target.value = '';
  }

  getSurveyTitle(): string {
    const survey = this.surveyDropdown.find(s => s.survey_id === this.selectedSurvey);
    return survey ? survey.survey_title : '';
  }

  goBack(): void {
    this.router.navigate(['admin/customer-survey-menu/feedback']);
  }
}